import { JOURNEY_ROLE } from "../../enums/scan";
import { uniqueJourneyRoutes } from "./journeyRouteIdentity";
import type { CandidateJourney } from "./journeyTypes";
import { canonicalConcernKey } from "./priorityFlowConcerns";
import { routeClusterKey } from "./scanModel";

const GENERATED_ROUTE_LIMIT = 5;

export function retainFlowRoutes(
  paths: CandidateJourney[],
  representative: CandidateJourney,
): CandidateJourney[] {
  const ordered = uniqueJourneyRoutes(paths).sort(compareRouteRank);
  const legacy = ordered.filter((path) => path.legacy);
  const generated = uniqueGeneratedVariants(
    ordered.filter((path) => !path.legacy),
  );
  const selectedGenerated = selectDiverseRoutes(
    generated,
    representative.legacy ? null : representative,
    GENERATED_ROUTE_LIMIT,
  );
  return uniqueJourneyRoutes([representative, ...legacy, ...selectedGenerated]);
}

function routeVariantKey(path: CandidateJourney): string {
  return [
    canonicalNonEntryRoutePageIds(path).join(":"),
    terminalPageId(path),
    canonicalRouteConcernKeys(path).join(":"),
    meaningfulClusterSequence(path).join(">"),
  ].join("|");
}

function meaningfulClusterSequence(path: CandidateJourney): string[] {
  const clusters = path.steps.flatMap((step) => {
    if (step.role === JOURNEY_ROLE.ENTRY) return [];
    const cluster = routeClusterKey(step.page.path);
    return cluster === "home" ? [] : [cluster];
  });
  return clusters.filter((cluster, index) => clusters[index - 1] !== cluster);
}

function uniqueGeneratedVariants(
  paths: CandidateJourney[],
): CandidateJourney[] {
  return [
    ...new Map(paths.map((path) => [routeVariantKey(path), path])).values(),
  ];
}

function selectDiverseRoutes(
  paths: CandidateJourney[],
  required: CandidateJourney | null,
  limit: number,
): CandidateJourney[] {
  const selected = required ? [required] : paths[0] ? [paths[0]] : [];
  const remaining = paths.filter((path) => path.id !== required?.id);
  while (selected.length < limit && remaining.length > 0) {
    remaining.sort(
      (left, right) =>
        diversityScore(right, selected) - diversityScore(left, selected) ||
        compareRouteRank(left, right),
    );
    selected.push(remaining.shift() as CandidateJourney);
  }
  return selected;
}

function diversityScore(
  path: CandidateJourney,
  selected: CandidateJourney[],
): number {
  const selectedTerminals = new Set(selected.map(terminalPageId));
  const selectedClusters = new Set(
    selected.map((route) => meaningfulClusterSequence(route).join(">")),
  );
  const selectedPages = new Set(
    selected.flatMap((route) => canonicalNonEntryRoutePageIds(route)),
  );
  const uniquePages = canonicalNonEntryRoutePageIds(path).filter(
    (pageId) => !selectedPages.has(pageId),
  ).length;
  const terminalRole = path.steps.at(-1)?.role;
  return (
    Number(!selectedTerminals.has(terminalPageId(path))) * 100 +
    Number(!selectedClusters.has(meaningfulClusterSequence(path).join(">"))) *
      40 +
    uniquePages * 10 +
    Number(
      terminalRole === JOURNEY_ROLE.ACTION ||
        terminalRole === JOURNEY_ROLE.DESTINATION,
    ) *
      4 -
    path.rank / 1000
  );
}

function canonicalNonEntryRoutePageIds(path: CandidateJourney): string[] {
  return [
    ...new Set(
      path.steps.flatMap((step) =>
        step.role === JOURNEY_ROLE.ENTRY ? [] : [step.page.id],
      ),
    ),
  ].sort();
}

function canonicalRouteConcernKeys(path: CandidateJourney): string[] {
  return [...new Set(path.concernCandidates.map(canonicalConcernKey))].sort();
}

function terminalPageId(path: CandidateJourney): string {
  return path.steps.at(-1)?.page.id ?? "none";
}

function compareRouteRank(
  left: CandidateJourney,
  right: CandidateJourney,
): number {
  return left.rank - right.rank || left.id.localeCompare(right.id);
}
